import type { CardKind, DayMode, EnergyLevel, LocalTime, When } from '@konvert/shared';
import type { WeekdayToken } from '@konvert/shared';
import {
  emptyDraft,
  parseClock,
  parseDuration,
  parseEnergy,
  type CardDraft,
} from './cardDraft.js';
import { triageSourceFor, type TriageSourceSpec } from './triage.js';

/**
 * A typed command line, the way a text adventure takes them (design note of
 * 2026-09-25). Deterministic and free: the assistant is only called for what
 * this parser does not recognise.
 */
export type SystemEnvelopeRef = 'inbox' | 'someday' | 'projects' | 'done' | 'trash' | 'notes_archive';

export type MoveTarget =
  | { kind: 'when'; when: When }
  | { kind: 'envelope'; envelope: SystemEnvelopeRef };

export type RoomRef =
  | { kind: 'day'; when: When | null }
  /** Every open card of one kind, wherever it lies (2026-10-06). */
  | { kind: 'kind'; cardKind: 'reminder' | 'meeting' | 'project' | 'note' }
  | { kind: 'week' }
  | { kind: 'month' }
  | { kind: 'year' }
  | { kind: 'envelope'; envelope: SystemEnvelopeRef }
  | { kind: 'carryover' }
  | { kind: 'map' };

export type LineIntent =
  | { kind: 'help' }
  | { kind: 'guide' }
  | { kind: 'open'; index: number }
  // `index: null` means "the card in front of you" — inside a card the verbs
  // need no number ("готово", "удали", "в чт").
  | { kind: 'complete'; index: number | null }
  | { kind: 'focus'; index: number | null }
  | { kind: 'drop'; index: number | null }
  | { kind: 'note'; index: number | null; text: string }
  | { kind: 'move'; index: number | null; target: MoveTarget }
  | { kind: 'show'; room: RoomRef }
  | { kind: 'undo' }
  | { kind: 'create'; draft: CardDraft }
  // Everything below exists so that nothing needs the Mini App (2026-09-29).
  | { kind: 'triage'; source: TriageSourceSpec['kind'] }
  | { kind: 'edit'; index: number | null; edit: CardEdit }
  | { kind: 'waiting'; index: number | null; text: string; until: When | null }
  | { kind: 'reopen'; index: number | null }
  | { kind: 'dayMode'; mode: DayMode }
  | { kind: 'settings'; change: SettingChange | null }
  | { kind: 'rules'; action: RuleAction; index: number | null }
  // Two screens the bot draws itself: everything it can do, and the drawer.
  | { kind: 'menu' }
  | { kind: 'envelopes' };

/** One field of one card, changed from the chat. */
export type CardEdit =
  | { field: 'title'; value: string }
  | { field: 'est_minutes'; value: number }
  | { field: 'energy'; value: EnergyLevel }
  | { field: 'deadline_date'; value: When | null }
  | { field: 'location'; value: string }
  | { field: 'start_time'; value: LocalTime }
  | { field: 'is_private'; value: boolean };

export type SettingChange =
  | { key: 'morning_push_time'; value: LocalTime }
  | { key: 'focus_interval_min'; value: number }
  | { key: 'day_limit_minutes'; value: number }
  | { key: 'day_limit_energy'; value: number }
  | { key: 'pushes_enabled'; value: boolean }
  | { key: 'ai_enabled'; value: boolean };

export type RuleAction = 'list' | 'enable' | 'disable' | 'drop';

const WEEKDAYS: Record<string, WeekdayToken> = {
  пн: 'mon', понедельник: 'mon', пон: 'mon',
  вт: 'tue', вторник: 'tue',
  ср: 'wed', среда: 'wed', среду: 'wed',
  чт: 'thu', четверг: 'thu',
  пт: 'fri', пятница: 'fri', пятницу: 'fri',
  сб: 'sat', суббота: 'sat', субботу: 'sat',
  вс: 'sun', воскресенье: 'sun', вскр: 'sun',
};

const ENVELOPES: Record<string, SystemEnvelopeRef> = {
  бэклог: 'inbox', беклог: 'inbox', входящие: 'inbox', инбокс: 'inbox',
  someday: 'someday', когда: 'someday', 'когда-нибудь': 'someday',
  проекты: 'projects', проект: 'projects',
  копилка: 'done', копилку: 'done', сделано: 'done',
  корзина: 'trash', корзину: 'trash', мусор: 'trash',
  архив: 'notes_archive',
};

/**
 * The vocabulary, as data. The parser builds its patterns from this table and
 * the bot builds its guide from it, so a new verb cannot exist undocumented —
 * a test checks both directions.
 */
export interface VerbSpec {
  intent: LineIntent['kind'];
  aliases: string[];
  example: string;
  description: string;
  /** What to type after an alias for it to mean anything; used by the tests. */
  args?: string;
}

/**
 * Editing one field of one card from the chat. Its own table because the six
 * verbs differ only in the field they set (2026-09-29).
 */
export interface EditVerbSpec {
  field: CardEdit['field'];
  aliases: string[];
  example: string;
  description: string;
}

export const EDIT_VERBS: EditVerbSpec[] = [
  {
    field: 'title',
    aliases: ['переименуй', 'назови'],
    example: 'переименуй 2 отзыв на иск',
    description: 'изменить название',
  },
  {
    field: 'est_minutes',
    aliases: ['время', 'оценка'],
    example: 'время 2 45м',
    description: 'изменить оценку времени',
  },
  {
    field: 'energy',
    aliases: ['энергия'],
    example: 'энергия 2 жёлтая',
    description: 'изменить цвет: зелёная, жёлтая, красная',
  },
  {
    field: 'deadline_date',
    aliases: ['дедлайн', 'срок'],
    example: 'дедлайн 2 25.10',
    description: 'поставить дедлайн; «дедлайн 2 нет» — убрать',
  },
  {
    field: 'start_time',
    aliases: ['начало', 'во сколько'],
    example: 'начало 2 14:30',
    description: 'сдвинуть время встречи (длительность сохраняется)',
  },
  {
    field: 'location',
    aliases: ['место'],
    example: 'место 2 Кнез Михайлова 5',
    description: 'изменить место встречи',
  },
  {
    field: 'is_private',
    aliases: ['спрячь', 'раскрой'],
    example: 'спрячь 2',
    description: 'скрыть карточку от ассистента («раскрой 2» — вернуть)',
  },
];

export const COMMAND_VERBS: VerbSpec[] = [
  {
    intent: 'open',
    aliases: [],
    example: '2',
    description: 'открыть карточку с этим номером',
  },
  {
    intent: 'complete',
    aliases: ['готово', 'гот', 'сделал', 'сделано'],
    args: '1',
    example: 'готово 2',
    description: 'отметить выполненной',
  },
  {
    intent: 'move',
    aliases: ['в'],
    args: 'чт 1',
    example: 'в чт 3',
    description: 'переложить: сегодня, завтра, пн…вс, дату, неделю, след неделю, месяц, бэклог, someday, корзину',
  },
  {
    intent: 'focus',
    aliases: ['фокус', 'ф'],
    args: '1',
    example: 'фокус 1',
    description: 'взять в фокус: бот будет спрашивать о ней по таймеру',
  },
  {
    intent: 'note',
    aliases: ['пометка', '!'],
    args: '1 звонил',
    example: 'пометка 2 звонил, ждут ответ',
    description: 'добавить пометку; она запомнит конверт, в котором карточка лежала',
  },
  {
    intent: 'drop',
    aliases: ['удали', 'удалить'],
    args: '1',
    example: 'удали 4',
    description: 'отправить в корзину',
  },
  {
    intent: 'create',
    aliases: ['+'],
    args: 'дело',
    example: '+ отзыв на иск 2ч жёлтая завтра',
    description:
      'создать задачу; «+ встреча», «+ заметка», «+ напомни», «+ проект» — другие виды. Чего не хватит — бот спросит',
  },
  {
    intent: 'show',
    aliases: [
      'день',
      'карта',
      'неделя',
      'месяц',
      'год',
      'бэклог',
      'someday',
      'проекты',
      'копилка',
      'корзина',
      'напоминания',
      'встречи',
      'заметки',
    ],
    example: 'карта',
    description:
      'перейти в конверт, на карту двух недель или к списку всех напоминаний, встреч, заметок',
  },
  { intent: 'undo', aliases: ['отмена', 'откат'], example: 'отмена', description: 'откатить последнее действие' },
  { intent: 'help', aliases: ['?', 'помощь', 'справка'], example: '?', description: 'короткая подсказка' },
  {
    intent: 'guide',
    aliases: ['гайд', 'инструкция'],
    example: 'гайд',
    description: 'полный гайд: как всё устроено и что можно делать',
  },
  {
    intent: 'triage',
    aliases: ['разбор', 'разобрать', 'по одной'],
    example: 'разбор',
    description:
      'разбирать по одной: на экране одна карточка, решение кнопкой, дальше следующая. «разбор бэклог», «разбор день» — другой конверт',
  },
  {
    intent: 'edit',
    aliases: [],
    example: 'время 2 45м',
    description: 'изменить поле карточки — см. таблицу ниже',
  },
  {
    intent: 'waiting',
    aliases: ['ждёт', 'ждет', 'ожидание'],
    args: '1 ответа',
    example: 'ждёт 2 ответа от Ивана до 10.10',
    description: 'пометить «ждёт»: карточка уходит из нагрузки и вернётся к дате проверки',
  },
  {
    intent: 'reopen',
    aliases: ['вернуть', 'верни'],
    args: '1',
    example: 'вернуть 1',
    description: 'вернуть карточку из Копилки или Корзины в конверт дня',
  },
  {
    intent: 'dayMode',
    aliases: ['режим'],
    args: 'обычный',
    example: 'режим тяжёлый',
    description: 'режим дня: обычный, тяжёлый, выходной — меняет лимит',
  },
  {
    intent: 'settings',
    aliases: ['настройки'],
    example: 'настройки',
    description:
      'показать настройки; переключатель строгого лимита дел находится кнопками в настройках; «настройки утро 8:30», «настройки фокус 25м», «настройки лимит 6ч», «настройки энергия 4», «настройки пуши выкл», «настройки ии выкл»',
  },
  {
    intent: 'rules',
    aliases: ['правила', 'правило'],
    example: 'правила',
    description: 'правила ассистента: список; «правило выкл 2», «правило вкл 2», «правило удали 2»',
  },
  {
    intent: 'menu',
    aliases: ['меню', 'функции', 'что ты умеешь'],
    example: 'меню',
    description: 'общее меню: все функции бота списком и кнопками',
  },
  {
    intent: 'envelopes',
    aliases: ['конверты', 'все конверты', 'ящик'],
    example: 'конверты',
    description: 'все конверты со счётчиками — окинуть взглядом',
  },
];

const VERB_ALIASES = (intent: LineIntent['kind']): string[] =>
  COMMAND_VERBS.find((verb) => verb.intent === intent)?.aliases ?? [];

const CARD_VERB_PATTERN = new RegExp(
  `^(${[...VERB_ALIASES('complete'), ...VERB_ALIASES('focus'), ...VERB_ALIASES('note'), ...VERB_ALIASES('drop')]
    .map((alias) => alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')})\\s+(\\d{1,2})\\b(.*)$`,
);

/** Parses one line. Returns null when it is not a command — then it is a message. */
export function parseCommandLine(input: string): LineIntent | null {
  const line = input.trim().replace(/\s+/g, ' ');
  if (line.length === 0) return null;
  const lower = line.toLowerCase();

  if (lower === 'гайд' || lower === 'инструкция' || lower === 'как это работает') {
    return { kind: 'guide' };
  }
  if (lower === '?' || lower === 'помощь' || lower === 'справка') return { kind: 'help' };
  if (VERB_ALIASES('menu').includes(lower)) return { kind: 'menu' };
  if (VERB_ALIASES('envelopes').includes(lower)) return { kind: 'envelopes' };
  if (lower === 'отмена' || lower === 'откат') return { kind: 'undo' };

  // A bare number opens that card.
  const bare = /^(\d{1,2})$/.exec(lower);
  if (bare) return { kind: 'open', index: Number(bare[1]) };

  if (lower.startsWith('+ ') || lower.startsWith('+')) {
    return parseCreate(line.replace(/^\+\s*/, ''));
  }

  // Card-by-card triage. «разбор списком» still shows the whole envelope.
  const triage = matchVerb(line, VERB_ALIASES('triage'));
  if (triage) {
    if (/^спис(ком|ок)$/.test(triage.rest.toLowerCase())) {
      return { kind: 'show', room: { kind: 'carryover' } };
    }
    const source = triage.rest.length > 0 ? triageSourceFor(triage.rest) : triageSourceFor('разбор');
    if (source) return { kind: 'triage', source: source.kind };
  }

  const settings = matchVerb(line, VERB_ALIASES('settings'));
  if (settings) {
    if (settings.rest.length === 0 && settings.index === null) return { kind: 'settings', change: null };
    const change = parseSettingChange(settings.index === null ? settings.rest : `${settings.index} ${settings.rest}`);
    if (change) return { kind: 'settings', change };
    return { kind: 'settings', change: null };
  }

  const rules = matchVerb(line, VERB_ALIASES('rules'));
  if (rules) {
    const parsed = parseRuleLine(rules);
    if (parsed) return parsed;
  }

  const mode = matchVerb(line, VERB_ALIASES('dayMode'));
  if (mode) {
    const parsed = parseDayMode(mode.rest);
    if (parsed) return { kind: 'dayMode', mode: parsed };
  }

  const reopen = matchVerb(line, VERB_ALIASES('reopen'));
  if (reopen) return { kind: 'reopen', index: reopen.index };

  const waiting = matchVerb(line, VERB_ALIASES('waiting'));
  if (waiting) {
    const parsed = parseWaiting(waiting);
    if (parsed) return parsed;
  }

  const edit = parseEdit(line);
  if (edit) return edit;

  const room = parseRoom(lower);
  if (room) return { kind: 'show', room };

  const verb = CARD_VERB_PATTERN.exec(lower);
  if (verb) {
    const index = Number(verb[2]);
    const rest = line.slice(line.toLowerCase().indexOf(verb[3] ?? '', verb[0].length - (verb[3]?.length ?? 0))).trim();
    // Which verb it was is decided by the alias tables, so adding an alias to
    // COMMAND_VERBS is enough.
    const alias = verb[1]!;
    if (VERB_ALIASES('complete').includes(alias)) return { kind: 'complete', index };
    if (VERB_ALIASES('focus').includes(alias)) return { kind: 'focus', index };
    if (VERB_ALIASES('drop').includes(alias)) return { kind: 'drop', index };
    if (VERB_ALIASES('note').includes(alias)) {
      return rest.length > 0 ? { kind: 'note', index, text: rest } : null;
    }
  }

  // The same verbs without a number: they act on the open card.
  if (VERB_ALIASES('complete').includes(lower)) return { kind: 'complete', index: null };
  if (VERB_ALIASES('focus').includes(lower)) return { kind: 'focus', index: null };
  if (VERB_ALIASES('drop').includes(lower)) return { kind: 'drop', index: null };
  for (const alias of VERB_ALIASES('note')) {
    const prefix = /^[a-zа-яё]+$/i.test(alias) ? `${alias} ` : alias;
    if (lower.startsWith(prefix)) {
      const text = line.slice(prefix.length).trim();
      if (text.length > 0) return { kind: 'note', index: null, text };
    }
  }

  // "в чт 3" and "3 в чт" both mean the same thing.
  const moveBefore = /^в\s+(.+?)\s+(\d{1,2})$/.exec(lower);
  const moveAfter = /^(\d{1,2})\s+в\s+(.+)$/.exec(lower);
  if (moveBefore || moveAfter) {
    const index = Number(moveBefore ? moveBefore[2] : moveAfter![1]);
    const target = parseTarget((moveBefore ? moveBefore[1] : moveAfter![2])!.trim());
    if (target) return { kind: 'move', index, target };
  }

  const bareMove = /^в\s+(.+)$/.exec(lower);
  if (bareMove) {
    const target = parseTarget(bareMove[1]!.trim());
    if (target) return { kind: 'move', index: null, target };
  }

  return null;
}

function parseRoom(lower: string): RoomRef | null {
  if (lower === 'день' || lower === 'сегодня' || lower === 'конверт') {
    return { kind: 'day', when: null };
  }
  // Cards of one kind, gathered from every envelope: a reminder for next month
  // lies in that day and is otherwise unfindable (bug report of 2026-10-06).
  if (['напоминания', 'напоминалки', 'напомнить'].includes(lower)) {
    return { kind: 'kind', cardKind: 'reminder' };
  }
  if (['встречи', 'календарь'].includes(lower)) return { kind: 'kind', cardKind: 'meeting' };
  if (lower === 'заметки') return { kind: 'kind', cardKind: 'note' };
  if (lower === 'завтра') return { kind: 'day', when: { kind: 'relative_days', days: 1 } };
  if (lower === 'карта' || lower === 'дни') return { kind: 'map' };
  if (lower === 'разбор') return { kind: 'carryover' };
  if (lower === 'неделя' || lower === 'неделю') return { kind: 'week' };
  if (lower === 'месяц') return { kind: 'month' };
  if (lower === 'год') return { kind: 'year' };

  const weekday = WEEKDAYS[lower];
  if (weekday) return { kind: 'day', when: { kind: 'weekday', weekday, which: 'next' } };

  const envelope = ENVELOPES[lower];
  if (envelope) return { kind: 'envelope', envelope };
  return null;
}

function parseTarget(text: string): MoveTarget | null {
  const when = parseWhen(text);
  if (when) return { kind: 'when', when };

  const envelope = ENVELOPES[text];
  if (envelope) return { kind: 'envelope', envelope };

  if (text === 'неделю' || text === 'неделя' || text === 'эту неделю') {
    return { kind: 'when', when: { kind: 'period', period: 'week', offset: 0 } };
  }
  if (text.startsWith('след') && text.includes('недел')) {
    return { kind: 'when', when: { kind: 'period', period: 'week', offset: 1 } };
  }
  if (text === 'месяц' || text === 'этот месяц') {
    return { kind: 'when', when: { kind: 'period', period: 'month', offset: 0 } };
  }
  if (text.startsWith('след') && text.includes('месяц')) {
    return { kind: 'when', when: { kind: 'period', period: 'month', offset: 1 } };
  }
  return null;
}

/** Date words the parser understands; the calendar maths stays in resolveWhen. */
export function parseWhen(text: string): When | null {
  const lower = text.toLowerCase();
  if (lower === 'сегодня') return { kind: 'relative_days', days: 0 };
  if (lower === 'завтра') return { kind: 'relative_days', days: 1 };
  if (lower === 'послезавтра') return { kind: 'relative_days', days: 2 };

  const weekday = WEEKDAYS[lower.replace(/^в\s+/, '')];
  if (weekday) return { kind: 'weekday', weekday, which: 'next' };

  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(lower);
  if (iso) return { kind: 'date', date: lower };

  // 25.09 or 25.09.2026
  const dotted = /^(\d{1,2})\.(\d{1,2})(?:\.(\d{4}))?$/.exec(lower);
  if (dotted) {
    const day = dotted[1]!.padStart(2, '0');
    const month = dotted[2]!.padStart(2, '0');
    const year = dotted[3] ?? String(new Date().getUTCFullYear());
    return { kind: 'date', date: `${year}-${month}-${day}` };
  }
  return null;
}

/**
 * `+ контрпредложение 2ч жёлтая чт`, `+ встреча с клиентом чт 11:00 1ч`,
 * `+ напомни проверить ответ пн`, `+ заметка роялти обычно 5–7%`.
 * Whatever is missing is asked for afterwards, not rejected.
 */
const KIND_WORDS: Record<string, CardKind> = {
  встреча: 'meeting', встречу: 'meeting', созвон: 'meeting', звонок: 'meeting',
  заметка: 'note', заметку: 'note', запиши: 'note', записка: 'note',
  напомни: 'reminder', напоминание: 'reminder', напомнить: 'reminder',
  проект: 'project',
  задача: 'task', задачу: 'task',
};

/**
 * `+ отзыв на иск 2ч жёлтая чт`. The parameters are read off the ENDS of
 * the line, never from the middle, and an ambiguous weekday abbreviation is a
 * date only when something else in that run is a parameter too or a preposition
 * makes it explicit. That is why «+ заплатить деньги за ПТ» keeps its «ПТ»
 * (bug of 2026-09-29): guessing what belongs to the title is worse than asking.
 */
function parseCreate(rest: string): LineIntent | null {
  const words = rest.split(' ').filter(Boolean);
  if (words.length === 0) return null;

  const first = words[0]!.toLowerCase();
  const kind = KIND_WORDS[first] ?? 'task';
  const body = KIND_WORDS[first] ? words.slice(1) : words;

  const draft = emptyDraft(kind);
  const fromStart = collectRun(draft, kind, body, 'forward');
  const fromEnd = collectRun(draft, kind, body.slice(fromStart), 'backward');

  draft.title = body.slice(fromStart, body.length - fromEnd).join(' ').trim();
  // A note keeps its whole text; the first line becomes the title later.
  return { kind: 'create', draft };
}

const DATE_PREPOSITIONS = ['в', 'во', 'на'];

/** Weekday spellings short enough to mean something else entirely («ПТ»). */
function isAmbiguousDateWord(lower: string): boolean {
  return WEEKDAYS[lower] !== undefined && lower.length <= 4;
}

/**
 * Eats parameters from one end of the line and returns how many words went.
 * Everything it does not recognise stops it, so the title in the middle is left
 * exactly as it was written.
 */
function collectRun(
  draft: CardDraft,
  kind: CardKind,
  words: readonly string[],
  direction: 'forward' | 'backward',
): number {
  let consumed = 0;
  /** Parameters that cannot be mistaken for a word of the title. */
  let solid = 0;
  let ambiguousDate: { viaPreposition: boolean } | null = null;

  for (;;) {
    const at = direction === 'forward' ? consumed : words.length - consumed - 1;
    if (at < 0 || at >= words.length || consumed >= words.length) break;

    // «в пт», «на 25.10», «в 14:30» — the preposition settles the question.
    const pairAt = direction === 'forward' ? at : at - 1;
    const pair =
      pairAt >= 0 && pairAt + 1 < words.length && DATE_PREPOSITIONS.includes(words[pairAt]!.toLowerCase())
        ? readParameter(draft, kind, words[pairAt + 1]!)
        : null;
    if (pair === 'when' || pair === 'clock') {
      if (pair === 'when' && isAmbiguousDateWord(words[pairAt + 1]!.toLowerCase())) {
        ambiguousDate = { viaPreposition: true };
      } else {
        solid += 1;
      }
      consumed += 2;
      continue;
    }

    const word = words[at]!.toLowerCase();
    const taken = readParameter(draft, kind, word);
    if (!taken) break;
    if (taken === 'when' && isAmbiguousDateWord(word)) ambiguousDate = { viaPreposition: false };
    else solid += 1;
    consumed += 1;
  }

  // A bare «пт» standing alone was probably part of the title after all.
  if (ambiguousDate && !ambiguousDate.viaPreposition && solid === 0) {
    draft.when = null;
    consumed -= 1;
  }
  return consumed;
}

type TakenParameter = 'clock' | 'duration' | 'energy' | 'when' | 'envelope' | null;

/** Assigns one word to the draft, or reports that it is not a parameter. */
function readParameter(draft: CardDraft, kind: CardKind, lower: string): TakenParameter {
  const clock = /^\d{1,2}[:.]\d{2}$/.test(lower) ? parseClock(lower) : null;
  if (clock && kind === 'meeting' && draft.startTime === null) {
    draft.startTime = clock;
    return 'clock';
  }
  if (clock && kind === 'reminder' && draft.remindTime === null) {
    draft.remindTime = clock;
    return 'clock';
  }

  const duration = /^\d{1,3}(ч|час|часа|часов|м|мин|минут)$/.test(lower) ? parseDuration(lower) : null;
  if (duration) {
    if (kind === 'meeting' && draft.durationMin === null) {
      draft.durationMin = duration;
      return 'duration';
    }
    if (kind !== 'meeting' && draft.estMinutes === null) {
      draft.estMinutes = duration;
      return 'duration';
    }
  }

  const energy = parseEnergy(lower);
  if (energy && draft.energy === null && kind === 'task') {
    draft.energy = energy;
    return 'energy';
  }

  const envelope = ENVELOPES[lower];
  if ((envelope === 'inbox' || envelope === 'someday') && draft.envelope === null && draft.when === null) {
    draft.envelope = envelope;
    return 'envelope';
  }

  const when = parseWhen(lower);
  if (when && draft.when === null && draft.envelope === null) {
    draft.when = when;
    return 'when';
  }
  return null;
}

/**
 * A verb, an optional card number and whatever follows: «время 2 45м»,
 * «время 45м» (the card in front of you), «настройки утро 8:30».
 */
interface VerbMatch {
  index: number | null;
  rest: string;
}

function matchVerb(line: string, aliases: readonly string[]): VerbMatch | null {
  const lower = line.toLowerCase();
  // Longest first, so «по одной» is not shadowed by a shorter alias.
  for (const alias of [...aliases].sort((a, b) => b.length - a.length)) {
    if (!lower.startsWith(alias)) continue;
    const after = line.slice(alias.length);
    if (after.length > 0 && !/^\s/.test(after)) continue;
    const rest = after.trim();
    // A number right after the verb is the card; «45м» or «25.10» is not.
    const num = /^(\d{1,2})(?=\s|$)/.exec(rest);
    if (num) return { index: Number(num[1]), rest: rest.slice(num[1]!.length).trim() };
    return { index: null, rest };
  }
  return null;
}

/**
 * Reads the value that follows the verb. When the value itself starts with a
 * number («время 45 мин»), that number was mistaken for a card number, so it is
 * given back to the value.
 */
function withValue<T>(
  match: VerbMatch,
  parse: (text: string) => T | null,
): { index: number | null; value: T } | null {
  const direct = parse(match.rest);
  if (direct !== null) return { index: match.index, value: direct };
  if (match.index !== null) {
    const combined = parse(`${match.index} ${match.rest}`.trim());
    if (combined !== null) return { index: null, value: combined };
  }
  return null;
}

function parseEdit(line: string): LineIntent | null {
  for (const verb of EDIT_VERBS) {
    const match = matchVerb(line, verb.aliases);
    if (!match) continue;

    switch (verb.field) {
      case 'title':
      case 'location': {
        const found = withValue(match, (text) => (text.length > 0 ? text : null));
        if (found) return { kind: 'edit', index: found.index, edit: { field: verb.field, value: found.value } };
        return null;
      }
      case 'est_minutes': {
        const found = withValue(match, parseDuration);
        if (found) return { kind: 'edit', index: found.index, edit: { field: 'est_minutes', value: found.value } };
        return null;
      }
      case 'energy': {
        const found = withValue(match, parseEnergy);
        if (found) return { kind: 'edit', index: found.index, edit: { field: 'energy', value: found.value } };
        return null;
      }
      case 'start_time': {
        const found = withValue(match, parseClock);
        if (found) return { kind: 'edit', index: found.index, edit: { field: 'start_time', value: found.value } };
        return null;
      }
      case 'deadline_date': {
        // «дедлайн 2 нет» takes the deadline off.
        if (/^(нет|убери|сними|никогда)$/.test(match.rest.toLowerCase())) {
          return { kind: 'edit', index: match.index, edit: { field: 'deadline_date', value: null } };
        }
        const found = withValue(match, parseWhen);
        if (found) return { kind: 'edit', index: found.index, edit: { field: 'deadline_date', value: found.value } };
        return null;
      }
      case 'is_private': {
        const hide = line.toLowerCase().startsWith('спрячь');
        return { kind: 'edit', index: match.index, edit: { field: 'is_private', value: hide } };
      }
    }
  }
  return null;
}

function parseWaiting(match: VerbMatch): LineIntent | null {
  if (match.rest.length === 0) return null;
  // A trailing «до 10.10» is the date to come back to it.
  const until = /^(.*?)\s+до\s+(\S+)$/.exec(match.rest);
  if (until) {
    const when = parseWhen(until[2]!);
    if (when) return { kind: 'waiting', index: match.index, text: until[1]!.trim(), until: when };
  }
  return { kind: 'waiting', index: match.index, text: match.rest, until: null };
}

function parseDayMode(text: string): DayMode | null {
  const lower = text.trim().toLowerCase();
  if (['обычный', 'обычн', 'норм', 'нормальный'].includes(lower)) return 'normal';
  if (['тяжёлый', 'тяжелый', 'трудный', 'сложный'].includes(lower)) return 'hard';
  if (['выходной', 'отдых', 'лёгкий', 'легкий', 'off'].includes(lower)) return 'off';
  return null;
}

function parseSettingChange(text: string): SettingChange | null {
  const [key = '', ...rest] = text.trim().toLowerCase().split(' ');
  const value = rest.join(' ').trim();
  const onOff = (): boolean | null =>
    ['вкл', 'включить', 'да', 'on'].includes(value) ? true : ['выкл', 'выключить', 'нет', 'off'].includes(value) ? false : null;

  if (['утро', 'бриф'].includes(key)) {
    const time = parseClock(value);
    return time ? { key: 'morning_push_time', value: time } : null;
  }
  if (key === 'фокус') {
    const minutes = parseDuration(value);
    return minutes ? { key: 'focus_interval_min', value: minutes } : null;
  }
  if (['лимит', 'часы'].includes(key)) {
    const minutes = parseDuration(value);
    return minutes ? { key: 'day_limit_minutes', value: minutes } : null;
  }
  if (['энергия', 'очки'].includes(key)) {
    const points = /^\d{1,2}$/.test(value) ? Number(value) : null;
    return points === null ? null : { key: 'day_limit_energy', value: points };
  }
  if (['пуши', 'уведомления'].includes(key)) {
    const flag = onOff();
    return flag === null ? null : { key: 'pushes_enabled', value: flag };
  }
  if (['ии', 'ассистент', 'ai'].includes(key)) {
    const flag = onOff();
    return flag === null ? null : { key: 'ai_enabled', value: flag };
  }
  return null;
}

function parseRuleLine(match: VerbMatch): LineIntent | null {
  if (match.rest.length === 0 && match.index === null) return { kind: 'rules', action: 'list', index: null };

  // «правило выкл 2» and «правило 2 выкл» mean the same thing.
  const ACTION = /^(выкл|выключи|выключить|вкл|включи|включить|удали|удалить)\s*(\d{1,2})?$/;
  const withAction = ACTION.exec(match.rest.toLowerCase());
  if (!withAction) return { kind: 'rules', action: 'list', index: null };

  const word = withAction[1]!;
  const index = withAction[2] ? Number(withAction[2]) : match.index;
  const action: RuleAction = word.startsWith('выкл') ? 'disable' : word.startsWith('вкл') ? 'enable' : 'drop';
  return { kind: 'rules', action, index };
}
